import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const ViewStaffLedger = () => {
  const { id } = useParams();
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [school, setSchool] = useState(null);

  // Get sessionId from localStorage
  const sessionId = localStorage.getItem('currentSessionId');
useEffect(() => {
  const fetchSchool = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/school/getSchool`);
      const data = await res.json();

      // assuming single school
      if (Array.isArray(data) && data.length > 0) {
        setSchool(data[0]);
      }
    } catch (err) {
      console.error("Failed to fetch school details", err);
    }
  };

  fetchSchool();
}, []);

  useEffect(() => {
    const fetchLedger = async () => {
      if (!sessionId) {
        setError("Session ID not found in localStorage.");
        setLoading(false);
        return;
      }

      try {
        // Fetch ledger based on the staffId (id) and sessionId
        const response = await fetch(` ${process.env.REACT_APP_API_BASE_URL}/api/ledgers/getLedgerByStaffId/${id}?sessionId=${sessionId}`);

        if (response.status === 404) {
          setError("No ledger found for this staff member.");
          setLoading(false);
          return;
        }

        if (!response.ok) throw new Error("Failed to fetch ledger.");

        const data = await response.json();
        setLedger(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLedger();
  }, [id, sessionId]);

  if (loading) return <div className="text-center p-10 text-xl">Loading...</div>;

  if (error)
    return (
      <div
        style={{
          color: "darkgray",
          fontSize: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
          fontWeight: "500"
        }}
      >
        {error}
      </div>
    );

  const { entries = [] } = ledger;

  // ----------------- PDF GENERATOR -----------------
 const generatePDF = () => {
  const doc = new jsPDF();

  let y = 20;

  /* ---------- SCHOOL HEADER ---------- */
  doc.setFontSize(18);
  doc.text(`${school?.schoolName || "School"} - Staff Ledger`, 14, y);
  y += 8;

  doc.setFontSize(11);
  if (school?.address) {
    doc.text(school.address, 14, y);
    y += 6;
  }

  if (school?.phone1 || school?.email1) {
    doc.text(
      `Phone: ${school?.phone1 || ""} | Email: ${school?.email1 || ""}`,
      14,
      y
    );
    y += 10;
  }

  /* ---------- STAFF INFO ---------- */
  const sessionName =
    typeof ledger.sessionId === "object"
      ? ledger.sessionId.sessionName
      : ledger.sessionId || "N/A";

  doc.setFontSize(12);
  doc.text(`Staff Name: ${ledger.name}`, 14, y);
  y += 6;

  doc.text(`Staff ID: ${ledger.staffId}`, 14, y);
  y += 6;

  doc.text(`Session: ${sessionName}`, 14, y);
  y += 10;

  /* ---------- LEDGER TABLE ---------- */
 const formatAmount = (amt) =>
  amt != null ? `Rs. ${amt.toLocaleString("en-IN")}` : "—";

doc.autoTable({
  startY: y,
  head: [["Date", "Receipt No", "Description", "Amount Paid", "Balance"]],
  body: entries.map((entry) => [
    new Date(entry.date).toLocaleDateString("en-IN"),
    entry.receiptNumber || "—",
    entry.description || "—",

    // Amount Paid (salary given to staff)
    entry.creditAmount
      ? formatAmount(entry.creditAmount)
      : "—",

    // Balance
    formatAmount(entry.balance),
  ]),
  styles: { fontSize: 10 },
  headStyles: { fillColor: [46, 204, 113] }, // green header
});


  doc.save(`staff_ledger_${ledger.staffId}.pdf`);
};


  return (
    <div className="p-8 bg-gray-100 min-h-screen">
      {/* ---------- Header ---------- */}
      <div className="text-center mb-10">
  {school?.logo && (
    <img
      src={school.logo}
      alt="School Logo"
      className="mx-auto mb-4 h-20"
    />
  )}

  <h1 className="text-4xl font-bold text-gray-800">
    {school?.schoolName}
  </h1>

  <p className="text-gray-700">{school?.address}</p>

  <p className="text-gray-700">
    Phone: {school?.phone1}
    {school?.email1 && ` | Email: ${school.email1}`}
  </p>
</div>


      {/* ---------- Staff Ledger Info ---------- */}
      <div className="bg-white p-8 rounded-xl shadow-md mb-10 border border-gray-300">
        <h2 className="text-xl font-semibold mb-6 text-gray-800">
          Staff Ledger – {ledger.name}
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-5 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Staff ID</p>
            <h3 className="text-sm font-semibold">{ledger.staffId}</h3>
          </div>

          <div className="p-5 bg-green-50 border border-green-200 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Session</p>
            <h3 className="text-sm font-semibold">{ledger.sessionId?.sessionName || "N/A"}</h3>
          </div>

          <div className="p-5 bg-yellow-50 border border-yellow-200 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Total Salary</p>
            <h3 className="text-sm font-semibold">
              ₹ {ledger.totalSalary?.toLocaleString("en-IN")}
            </h3>
          </div>

          <div className="p-5 bg-red-50 border border-red-200 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Base Salary</p>
            <h3 className="text-sm font-semibold">
              ₹ {ledger.baseSalary?.toLocaleString("en-IN")}
            </h3>
          </div>

          <div className="p-5 bg-indigo-50 border border-indigo-200 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Allowance</p>
            <h3 className="text-sm font-semibold">
              ₹ {ledger.allowance?.toLocaleString("en-IN")}
            </h3>
          </div>

           <div className="p-5 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
            <p className="text-sm text-gray-500">Advance</p>
            <h3 className="text-sm font-semibold">{ledger.totalSalary-ledger.balance}</h3>
          </div>


          <div className="p-5 bg-teal-50 border border-teal-200 rounded-lg shadow-sm col-span-full">
            <p className="text-sm text-gray-500">Current Balance</p>
            <h3 className="text-md font-bold text-gray-900">
              ₹ {ledger.balance?.toLocaleString("en-IN")}
            </h3>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-4 mt-6">
          <Link
            to={`/teachers/${ledger.staffId}`}
            className="bg-blue-600 text-white py-2 px-5 rounded-lg shadow hover:bg-blue-700"
          >
            View Staff Profile
          </Link>

          <button
            onClick={generatePDF}
            className="bg-red-600 text-white py-2 px-5 rounded-lg shadow hover:bg-red-700"
          >
            Generate PDF
          </button>
        </div>
      </div>

      {/* ---------- Ledger Table ---------- */}
      <div className="bg-white p-6 rounded-xl shadow-md border border-gray-300">
        <h3 className="text-2xl font-semibold mb-4 text-gray-800">Ledger Entries</h3>

        <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              {["Date", "Receipt No", "Description", "Amount Paid","Balance Due", "Actions"]
                .map((h, idx) => (
                  <th key={idx} className="py-3 px-4 border-b text-left font-semibold">
                    {h}
                  </th>
                ))}
            </tr>
          </thead>

          <tbody>
           {entries.length ? (
  entries.map((entry, index) => (
    <tr key={index} className="border-b hover:bg-gray-50">
      {/* Date */}
      <td className="py-3 px-4">
        {new Date(entry.date).toLocaleDateString()}
      </td>

      {/* Receipt No */}
      <td className="py-3 px-4">
        {entry.receiptNumber || "—"}
      </td>

      {/* Description */}
      <td className="py-3 px-4">
        {entry.description}
      </td>

      {/* Amount Paid (School → Teacher) */}
      <td className="py-3 px-4 font-medium">
        {entry.creditAmount != null
          ? `₹ ${entry.creditAmount.toLocaleString("en-IN")}`
          : "—"}
      </td>

      {/* Balance Due */}
      <td className="py-3 px-4">
        ₹ {entry.balance.toLocaleString("en-IN")}
      </td>

      {/* Actions */}
      <td className="py-3 px-4">
        {entry.receiptNumber && (
          <Link
            to={`/fee-receipt-details/${entry.receiptNumber}`}
            className="bg-blue-600 text-white py-1 px-3 rounded-md text-sm hover:bg-blue-700"
          >
            View
          </Link>
        )}
      </td>
    </tr>
  ))
) : (
  <tr>
    <td colSpan="6" className="py-4 text-center text-gray-500">
      No entries found
    </td>
  </tr>
)}

          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ViewStaffLedger;
